import { useState, useEffect } from 'react';
import { FiBarChart2 } from "react-icons/fi";
import { HiOutlineCube } from "react-icons/hi2";
import { IoSettingsOutline, IoPulseOutline } from "react-icons/io5";
import { MdKeyboardArrowUp } from "react-icons/md";
import { MdKeyboardArrowDown } from "react-icons/md";
import { GoPeople } from "react-icons/go";
import { HiOutlineDatabase, HiOutlineDocumentReport } from "react-icons/hi";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import { FiHome } from "react-icons/fi";
import { IoChatboxOutline } from "react-icons/io5";
import { GoQuestion } from "react-icons/go";
import { BookOpen, Code, FolderClosed, KeyRound, Mic, History, BarChart3, Settings, TrendingUp, Users, Plus, Zap, Clock, Phone } from 'lucide-react';
import { MdOutlineSupportAgent } from "react-icons/md";
import { IoMdAdd } from "react-icons/io";
import ModernTimeDropdownV2, { getSelectedOptionLabel } from '../dashboard-components/layouts/modernDropdownv2';
import server from '../api/server';
import { FaQuestion, FaRegCalendarAlt } from 'react-icons/fa';
import { clearNotificationsAccessTokenLastOkCookie } from '../utils/dashboardNotifAccessTokenCookie.js';

const buildClientOptions = (clients, legacyClients = []) => {
  if (Array.isArray(clients) && clients.length > 0) {
    return clients.map((client) => ({
      value: client.client_id,
      label: client.organisation_name || client.client_id,
      hideId: true,
    }));
  }
  const legacyClientIds = (Array.isArray(legacyClients) ? legacyClients : [])
    .map((client) => client[0])
    .filter(Boolean);
  return [...new Set(legacyClientIds)].map((clientId) => ({
    value: clientId,
    label: clientId,
  }));
};

const Sidebar = ({ setSelectedMenu, setOpenContact, setSubSelectedMenu, selectedOption, setActiveDashboardNum, selectedMenu, subSelectedMenu, selectedClientId, setSelectedClientId }) => {
  const [clientIdOptions, setClientIdOptions] = useState([])
  const [isAdmin, setIsAdmin] = useState(false)
  const [clientsData, setClientsData] = useState([])
  const [selectedPlatform, setSelectedPlatform] = useState('analytics')
  const [role] = useState(()=> {
    const userCookie = getCookie('dashboard_login_user_creds');
    if (userCookie) {
      const userData = JSON.parse(userCookie);
      const role = userData.role
      return role
    }
    return 'admin';
  })
  const [voiceEnabled, setVoiceEnabled] = useState(false)
  function getCookie(name) {
    function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
    var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
    return match ? match[1] : null;
  }

  const get_all_clients = async (user) => {
    let email = user.email
    let client_id = user.client_id
    let service_id = user.service_id
    try {
      let res = await server.get_all_clients(client_id, service_id, email, role, 'home', undefined)
      if(res.status === 200){
        let resp = await res.json()
        setClientsData(resp.data)
        const opts = buildClientOptions(resp.clients, resp.data);
        setClientIdOptions(opts);
        if(opts.length <=1){
          setIsAdmin(false)
        }else{
          setIsAdmin(true)
        }
      }
    } catch (error) {
    }
  }
  const check_voice_enabled = async (user) => {
    let email = user.email
    let client_id = user.client_id
    let service_id = user.service_id
    try {
      let res = await server.check_voice_enabled(client_id, service_id, email, 'home', undefined)
      if(res.status === 200){
        let resp = await res.json()
        if(resp.flag === true){
          setVoiceEnabled(true)
        }else{
          setVoiceEnabled(false)
        }
      }
    } catch (error) {
    }
  }

  useEffect(() => {
    try {
      const userCookie = getCookie('dashboard_login_user_creds');
      if (!userCookie) return;
      
      const user = JSON.parse(userCookie);
      let checkAdmin = user.role;
      
      if (checkAdmin) {
        get_all_clients(user)
        check_voice_enabled(user)
      }
    } catch (error) {
      console.error("Error parsing user data:", error);
    }
  }, [selectedOption, setSelectedMenu, setSubSelectedMenu]);

  useEffect(() => {
    const refreshDisplayNames = (event) => {
      const detail = event.detail || {};
      if (detail.entity_type !== 'organization') return;
      setClientIdOptions((options) => options.map((option) => (
        option.value === detail.client_id ? { ...option, label: detail.name } : option
      )));
    };
    window.addEventListener('displayNamesUpdated', refreshDisplayNames);
    return () => window.removeEventListener('displayNamesUpdated', refreshDisplayNames);
  }, []);

  useEffect(()=> {
    if(selectedClientId){
      const userCookie = getCookie('dashboard_login_user_creds');
      if (!userCookie) return;
      
      const user = JSON.parse(userCookie);
      let checkAdmin = user.role;
      
      if (checkAdmin) {
        check_voice_enabled(user)
      }
    }
  }, [selectedClientId])

  const [expandedSections, setExpandedSections] = useState({
    'Project': true,
    'Organization': true,
    'Analytics': false,
    'Agent Feedback': false,
    'Custom Analytics': false,
    'Developer Hub': false
  });

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  const renderArrow = (section) => {
    return (
      <span className="ml-1">
        {expandedSections[section] ? <MdKeyboardArrowUp/> : <MdKeyboardArrowDown/>}
      </span>
    );
  };

  // Map menu items to their parent sections
  const menuToSection = {
    'Home': ['Project'],
    'Agent Feedback Overall': ['Project', 'Analytics', 'Agent Feedback'],
    'Agent Feedback Agent Wise': ['Project', 'Analytics', 'Agent Feedback'],
    'Agent Feedback Team Wise': ['Project', 'Analytics', 'Agent Feedback'],
    'Agent Feedback Agent Training': ['Project', 'Analytics', 'Agent Feedback'],
    'Agent Feedback Reports': ['Project', 'Analytics', 'Agent Feedback'],
    'Customer Insights': ['Project', 'Analytics'],
    'Custom Reports': ['Project'],
    'Conversations': ['Project'],
    'Concern Query': ['Project', 'Analytics'],
    'Customers': ['Project'],
    'Events': ['Project'],
    'Event ID': ['Project'],
    'Apps': ['Project'],
    'Data Sources': ['Project'],
    'Project Settings': ['Project'],
    'Billing': ['Organization'],
    'Organization Settings': ['Organization'],
    'Configuration': ['Organization', 'Developer Hub'],
    'Documentation': ['Organization', 'Developer Hub']
  };

  // When selectedMenu changes, ensure the appropriate sections are expanded
  useEffect(() => {
    if (selectedMenu && menuToSection[selectedMenu]) {
      const sectionsToExpand = menuToSection[selectedMenu];
      
      // Create a new expanded sections object
      const newExpandedSections = { ...expandedSections };
      
      // Expand all parent sections needed for this menu item
      sectionsToExpand.forEach(section => {
        newExpandedSections[section] = true;
      });
      
      setExpandedSections(newExpandedSections);
    }
  }, [selectedMenu]);
  
  const update_cookie = (client_id) => {
    let role = 'admin'
    for (let i=0;i<clientsData.length;i++){
      if(client_id === clientsData[i][0]){
        role = clientsData[i][1]
        break;
      }
    }
    const userCookie = getCookie('dashboard_login_user_creds');
    const user = JSON.parse(userCookie);
    const userData = {
      email: user.email,
      client_id: client_id,
      service_id: user.service_id,
      loginTime: new Date().getTime(),
      role: role,
      name: user.name,
      'session_id': user.session_id
    };
  
    // Store updated user data in cookies (expires in 3 days)
    clearNotificationsAccessTokenLastOkCookie();
    document.cookie = "dashboard_login_user_creds=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; SameSite=None";
    const currentDate = new Date();

    // Set the expiry date to 30 days from now
    currentDate.setTime(currentDate.getTime() + (30 * 24 * 60 * 60 * 1000));

    // Format the expiry date in UTC format
    const expires = `expires=${currentDate.toUTCString()}`;
    document.cookie = `dashboard_login_user_creds=${JSON.stringify(userData)}; ${expires}; path=/; Secure; SameSite=None`;
  }
  
  const handleClientIdChange = (e) => {
    const newClientId = e.target.value;
    update_cookie(newClientId)
    setSelectedClientId(newClientId);
  };

  const handlePlatformChange = (e) => {
    const selectedLabel = e.target.value;
    const newPlatform = selectedLabel === 'Voice Agents Platform' ? 'voice_agents' : 'analytics';
    setSelectedPlatform(newPlatform);
    if (newPlatform === 'voice_agents') {
      setSelectedMenu('VA Home');
    } else {
      setSelectedMenu('Home');
    }
  };

  // Auto-switch platform based on selected menu from URL/navigation
  useEffect(() => {
    if (selectedMenu && selectedMenu.startsWith && selectedMenu.startsWith('VA ')) {
      if (selectedPlatform !== 'voice_agents') setSelectedPlatform('voice_agents');
    } else {
      if (selectedPlatform !== 'analytics') setSelectedPlatform('analytics');
    }
  }, [selectedMenu]);

  const renderAnalyticsPlatform = () => (
    <div>
      {/* Project Section */}
      <div>
        <div 
          className="p-2 font-medium text-gray-700 flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection('Project')} 
          style={{fontSize: "15px", padding: "1rem", fontWeight: "600", color: "black"}}
        >
          Project {renderArrow('Project')}
        </div>
        
        {expandedSections['Project'] && (
          <div className="pl-1 pt-1 pb-1">
            <div onClick={() => setSelectedMenu('Home')} className={`flex items-center py-2 px-4 text-gray-700 hover:bg-gray-100 hover:rounded cursor-pointer ${selectedMenu === 'Home' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`} style={{fontSize: "13px", marginRight: "10px", color: "black"}}>
               <FiHome style={{color: "black", marginRight: "10px", fontSize: "large"}}/> Home
            </div>
            
            <div 
              className="flex items-center justify-between py-2 px-4 text-gray-700 hover:bg-gray-100 hover:rounded cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('Analytics');
              }} 
              style={{fontSize: "13px", marginRight: "10px", color: "black"}}
            >
              <div className="flex items-center">
                <FiBarChart2 style={{color: "black", marginRight: "10px", fontSize: "large"}}/> Analytics
              </div>
              {renderArrow('Analytics')}
            </div>
            
            {expandedSections['Analytics'] && (
              <div className="ml-8">
                <div 
                  className="flex items-center justify-between py-2 px-4 text-gray-700 hover:bg-gray-100 cursor-pointer hover:rounded"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSection('Agent Feedback');
                  }} 
                  style={{fontSize: '13px', marginRight: "10px", color: "black"}}
                >
                 <span style={{display: "flex", alignItems: "center"}}><MdOutlineSupportAgent style={{ color: 'black', marginRight: '10px', fontSize: "large" }} /> Agent Feedback</span> {renderArrow('Agent Feedback')}
                </div>
                
                {expandedSections['Agent Feedback'] && (
                  <div className="ml-4">
                    <div className={`flex items-center py-2 px-6 text-gray-700 cursor-pointer ${selectedMenu === 'Agent Feedback Overall' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`} 
                    style={{fontSize: "13px", marginRight: "10px", color: "black"}} onClick={() => setSelectedMenu('Agent Feedback Overall')}>Overview</div>
                    <div className={`flex items-center py-2 px-6 text-gray-700 cursor-pointer ${selectedMenu === 'Agent Feedback Agent Wise' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`}
                    style={{fontSize: "13px", marginRight: "10px", color: "black"}} onClick={() => setSelectedMenu('Agent Feedback Agent Wise')}>Agent Comparison</div>
                    <div className={`flex items-center py-2 px-6 text-gray-700 cursor-pointer ${selectedMenu === 'Agent Feedback Team Wise' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`}
                    style={{fontSize: "13px", marginRight: "10px", color: "black"}} onClick={() => setSelectedMenu('Agent Feedback Team Wise')}>Team Comparison</div>
                    <div className={`flex items-center py-2 px-6 text-gray-700 cursor-pointer ${selectedMenu === 'Agent Feedback Reports' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`}
                    style={{fontSize: "13px", marginRight: "10px", color: "black"}} onClick={() => setSelectedMenu('Agent Feedback Reports')}>Reports</div>
                    <div className={`flex items-center py-2 px-6 text-gray-700 cursor-pointer ${selectedMenu === 'Agent Feedback Agent Training' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`} 
                    style={{fontSize: "13px", marginRight: "10px", color: "black"}} onClick={() => setSelectedMenu('Agent Feedback Agent Training')}>Training</div>
                  </div>
                )}
                <div className={`flex items-center py-2 px-4 text-gray-700 cursor-pointer ${selectedMenu === 'Customer Insights' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`} 
                    style={{fontSize: "13px", marginRight: "10px", color: "black"}} onClick={() => setSelectedMenu('Customer Insights')}><FiBarChart2 style={{color: "black", marginRight: "10px", fontSize: "large"}}/> Customer Insights</div>
                <div className={`flex items-center py-2 px-4 text-gray-700 cursor-pointer ${selectedMenu === 'Concern Query' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`} 
                    style={{fontSize: "13px", marginRight: "10px", color: "black"}} onClick={() => setSelectedMenu('Concern Query')}><AiOutlineExclamationCircle style={{color: "black", marginRight: "10px", fontSize: "large"}}/> Concern & Queries</div>
              </div>
            )}
          </div>
        )}
      </div>

      {expandedSections['Project'] && (
        <div className="pl-4 pb-1">
          <div
            className={`flex items-center py-2 px-1 text-gray-700 cursor-pointer ${selectedMenu === 'Custom Reports' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`}
            style={{ fontSize: '13px', marginRight: "10px", color: "black" }}
            onClick={() => setSelectedMenu('Custom Reports')}
          >
            <HiOutlineDocumentReport style={{ color: 'black', marginRight: '10px', fontSize: "large" }} /> Custom Reports
          </div>
          <div
            className={`flex items-center py-2 px-1 text-gray-700 cursor-pointer ${selectedMenu === 'Conversations' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`}
            style={{ fontSize: '13px', marginRight: "10px", color: "black" }}
            onClick={() => setSelectedMenu('Conversations')}
          >
            <IoChatboxOutline style={{ color: 'black', marginRight: '10px', fontSize: "large" }} /> Conversations
          </div>
          <div
            className={`flex items-center py-2 px-1 text-gray-700 cursor-pointer ${selectedMenu === 'Customers' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`}
            style={{ fontSize: '13px', marginRight: "10px", color: "black" }}
            onClick={() => setSelectedMenu('Customers')}
          >
            <GoPeople style={{ color: 'black', marginRight: '10px', fontSize: "large" }} /> Customers
          </div>
          <div
            className={`flex items-center py-2 px-1 text-gray-700 cursor-pointer ${['Events', 'Event ID'].includes(selectedMenu) ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`}
            style={{ fontSize: '13px', marginRight: "10px", color: "black" }}
            onClick={() => setSelectedMenu('Events')}
          >
            <IoPulseOutline style={{ color: 'black', marginRight: '10px', fontSize: "large" }} /> Events
          </div>

          <div
            className={`flex items-center py-2 px-1 text-gray-700 cursor-pointer ${selectedMenu === 'Apps' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`}
            style={{ fontSize: '13px', marginRight: "10px", color: "black" }}
            onClick={() => setSelectedMenu('Apps')}
          >
            <HiOutlineCube style={{ color: 'black', marginRight: '10px', fontSize: "large" }} /> Apps
          </div>

          <div
            className={`flex items-center py-2 px-1 text-gray-700 cursor-pointer ${selectedMenu === 'Data Sources' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`}
            style={{ fontSize: '13px', marginRight: "10px", color: "black" }}
            onClick={() => setSelectedMenu('Data Sources')}
          >
            <HiOutlineDatabase style={{ color: 'black', marginRight: '10px', fontSize: "large" }} /> Data Sources
          </div>
          <div className={`flex items-center py-2 px-1 text-gray-700 cursor-pointer ${selectedMenu === 'Project Settings' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`}
            style={{ fontSize: '13px', marginRight: "10px", color: "black" }}
            onClick={() => setSelectedMenu('Project Settings')}>
                <IoSettingsOutline style={{color: "black", marginRight: "10px", fontSize: "large"}}/> Settings
          </div>
        </div>
      )}
      
      {/* Organization Section */}
      <div className='border-t border-gray-200'>
        <div className="p-2 font-medium text-gray-700 flex justify-between items-center cursor-pointer"
          onClick={() => toggleSection('Organization')} 
          style={{fontSize: "15px", padding: "1rem", fontWeight: "600", color: "black"}}>
          Organization {renderArrow('Organization')}
        </div>
        
        {expandedSections['Organization'] && (
          <div className="ml-1">
            <div className={`flex items-center py-2 px-4 text-gray-700 cursor-pointer ${selectedMenu === 'Billing' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`}
              style={{ fontSize: '13px', marginRight: "10px", color: "black" }} onClick={() => setSelectedMenu('Billing')}>
              <FolderClosed style={{color: "black", marginRight: "6px", height: "22px", paddingRight: "7px"}}/> Billing
            </div>
            <div className={`flex items-center py-2 px-4 text-gray-700 cursor-pointer ${selectedMenu === 'Organization Settings' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`}
              style={{ fontSize: '13px', marginRight: "10px", color: "black" }} onClick={() => setSelectedMenu('Organization Settings')}>
              <IoSettingsOutline style={{color: "black", marginRight: "10px", fontSize: "large"}}/> Settings
            </div>
            <div
                className={`flex items-center py-2 px-4 text-gray-700 cursor-pointer hover:bg-gray-100 hover:rounded`}
                style={{ fontSize: '13px', marginRight: "10px", justifyContent: "space-between", color: "black" }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSection('Developer Hub');
                }}
              >
              <span style={{display: "flex", alignItems: "center"}}><Code style={{ marginRight: "7px", height: "19px", marginLeft: "-3px" }} /> Developer Hub</span> {renderArrow('Developer Hub')}
            </div>
            {expandedSections['Developer Hub'] && (
              <div className="ml-4">
                <div onClick={()=> setSelectedMenu('Configuration')} className={`flex items-center py-2 px-4 text-gray-700 cursor-pointer ${selectedMenu === 'Configuration' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`} 
                  style={{fontSize: "13px", marginRight: "10px", color: "black"}}><KeyRound style={{ color: 'black', marginRight: '10px', height: "19px" }}  /> Configuration</div>
                <div onClick={()=> setSelectedMenu('Documentation')} className={`flex items-center py-2 px-4 text-gray-700 cursor-pointer ${selectedMenu === 'Documentation' ? 'bg-gray-200 rounded' : 'hover:bg-gray-100 hover:rounded'}`} 
                  style={{fontSize: "13px", marginRight: "10px", color: "black"}}><BookOpen style={{ marginRight: '10px', height: "19px", marginLeft: "-3px" }} /> Documentation</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderVoiceAgentsPlatform = () => {
    const voiceAgentsMenuItems = [
      { 
        items: [
          { name: 'VA Home', label: 'Home', icon: FiHome, current: selectedMenu === 'VA Home' },
          { name: 'VA Agents', label: 'Agents', icon: Users, current: selectedMenu === 'VA Agents' },
          { name: 'VA Conversations', label: 'Conversations', icon: IoChatboxOutline, current: selectedMenu === 'VA Conversations' },
          { name: 'VA Customers', label: 'Customers', icon: GoPeople, current: selectedMenu === 'VA Customers' },
          { name: 'VA Campaigns', label: 'Campaigns', icon: FaRegCalendarAlt, current: selectedMenu === 'VA Campaigns' },
          { name: 'VA Phone Number', label: 'Phone Numbers', icon: Phone, current: selectedMenu === 'VA Phone Number' },
          ...(['superadmin', 'admin', 'manager'].includes(String(role).toLowerCase()) ? [
            { name: 'VA Widgets', label: 'Widgets', icon: HiOutlineCube, current: selectedMenu === 'VA Widgets' },
          ] : []),
          { name: 'VA Knowledge Base', label: 'Knowledge Base', icon: BookOpen, current: selectedMenu === 'VA Knowledge Base' },
        ]
      }
    ];

    return (
      <div className="px-2">
        {voiceAgentsMenuItems.map((category, categoryIndex) => (
          <div key={category.category} className="mb-6 mt-2">
            <nav className="space-y-1">
              {category.items.map((item) => (
                <div
                  key={item.name}
                  onClick={() => setSelectedMenu(item.name)}
                  className={`${
                    item.current
                      ? 'bg-gray-200 border-gray-300 text-gray-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:rounded'
                  } group flex items-center px-3 py-2 text-sm font-medium rounded-md cursor-pointer transition-colors duration-150`}
                >
                  <item.icon
                    className={`${
                      item.current ? 'text-gray-700' : 'text-gray-400 group-hover:text-gray-500'
                    } mr-3 h-4 w-4`}
                    aria-hidden="true"
                  />
                  {item.label || item.name}
                </div>
              ))}
            </nav>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="w-64 bg-white border-r-2 border-gray-300" style={{width: "17%"}}>
      {/* Client ID Section */}
      <div style={{display: "flex",alignItems: "center",paddingLeft: "1rem"}} className='border-b border-gray-200 h-16'>
        {selectedClientId && !isAdmin && (
          <h2 style={{fontSize: "17px"}}>
            {getSelectedOptionLabel(clientIdOptions, selectedClientId).toUpperCase()}
          </h2>
        )}
        {isAdmin && (
          <div>
            <ModernTimeDropdownV2 
              placeholder="Select Client ID" 
              value={selectedClientId} 
              onChange={handleClientIdChange} 
              options={clientIdOptions}
            />
          </div>
        )}
      </div>
      
      <div style={{height: (voiceEnabled) ? window.innerHeight - 220 : window.innerHeight - 120, maxHeight: (voiceEnabled) ? window.innerHeight - 220 : window.innerHeight - 120, overflowY: "auto"}}>
        {selectedPlatform === 'analytics' ? renderAnalyticsPlatform() : renderVoiceAgentsPlatform()}
      </div>

      {/* Platform Switcher - moved to bottom */}
      {voiceEnabled && <div className="border-t border-gray-200 p-3">
        <div className="mb-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Platform</label>
          <ModernTimeDropdownV2 
            placeholder="Select Platform" 
            value={selectedPlatform === 'analytics' ? 'Analytics Dashboard' : 'Voice Agents Platform'} 
            onChange={handlePlatformChange} 
            options={['Analytics Dashboard', 'Voice Agents Platform']}
          />
        </div>
      </div>}

      {/* Floating Contact Button */}
      <button
        onClick={() => setOpenContact(true)}
        aria-label="Contact Us"
        className="fixed"
        style={{
          right: '15px',
          bottom: '20px',
          width: '25px',
          height: '25px',
          borderRadius: '9999px',
          backgroundColor: 'black',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
          zIndex: 50,
          cursor: 'pointer'
        }}
      >
        <FaQuestion style={{ width: '12px', height: '12px' }} />
      </button>
      
      {/* Anthropod Logo */}
      <div>
        <div className="border-t border-gray-200">
          <div className="ml-2">
            <div className={`flex items-center py-1 text-gray-700`}
            style={{ fontSize: '13px', padding: "0.5rem" }}>
              <img src="/assets/anthro_logo_1.svg" alt="" style={{height: "40px"}}/>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
